import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/Breadcrumb";
import { Button, Input, Modal, Table } from "antd";
import {
  // CheckCircleOutlined,
  // CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  ADD_AMENITY,
  UPDATE_AMENITY,
  GET_ALL_COMPANY_AMENITIES,
} from "../../graphql/companyQueries";
import { useMutation, useQuery } from "@apollo/client";
import Toast from "../../components/Toast/Toast";
import { useParams } from "react-router-dom";
import {
  EmptyState,
  ErrorBanner,
  ErrorState,
  skeletonTableProps,
} from "../../components/states";
import { useToast } from "../../components/Toast/toastHook";
import useDebounce from "../../hooks/useDebounce";
import TableToolbar from "../../components/TableToolbar/TableToolbar";
import { toServerSort } from "../../utils/tableSort";
import useConfirmDialog from "../../components/ConfirmDialog/useConfirmDialog";
import { TABLE_SCROLL } from "../../constants/table";

// Columns the server can order the amenities by.
const SORTABLE_COLUMNS = ["title"];

const Amenities = (props) => {
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalCount: 0,
  });
  const { companyId } = useParams();
  const [open, setOpen] = useState(false);
  const [selectedAmenity, setSelectedAmenity] = useState("");
  const [selectAmenityId, setSelectAmenityId] = useState(null);
  const [viewMode, setViewMode] = useState(null);
  // const [currentAmenity, setCurrentAmenity] = useState(null);

  const [searchText, setSearchText] = useState("");
  const debounceValue = useDebounce(searchText, 500);
  const [sort, setSort] = useState(null);

  const [amenities, setAmenities] = useState([]);

  const toast = useToast(4000);
  const { confirm, dialog } = useConfirmDialog();

  const handleOk = () => {
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
    setViewMode(null);
  };

  const showModal = () => {
    setOpen(true);
  };

  // const checkIsActive = (record) => {
  //   if (record.isActive) {
  //     return (
  //       <div style={{ display: "flex", justifyContent: "center" }}>
  //         <CheckCircleOutlined style={{ fontSize: "20px", color: "green" }} />
  //       </div>
  //     );
  //   } else {
  //     return (
  //       <div style={{ display: "flex", justifyContent: "center" }}>
  //         <CloseCircleOutlined style={{ fontSize: "20px", color: "red" }} />
  //       </div>
  //     );
  //   }
  // };

  const { loading, error, data, refetch } = useQuery(
    GET_ALL_COMPANY_AMENITIES,
    {
      variables: {
        companyId,
        pagination: {
          page: pagination.page,
          limit: pagination.limit,
        },
        searchText: debounceValue,
        ...(sort && { sort }),
      },
      fetchPolicy: "no-cache",
      onError(err) {
        Toast(
          "error",
          "There was an error loading the users. If the issue persists please contact the administrators"
        );
      },
    }
  );

  useEffect(() => {
    if (data?.getCompanyAmenitiesWithPagination?.amenities) {
      setAmenities(data?.getCompanyAmenitiesWithPagination?.amenities);
      setPagination((prev) => ({
        ...prev,
        totalCount: data?.getCompanyAmenitiesWithPagination?.totalCount,
      }));
    }
  }, [data]);

  const [addAmenity, { loading: addAmenityLoading }] = useMutation(
    ADD_AMENITY,
    {
      fetchPolicy: "no-cache",
      onCompleted(result) {
        setAmenities((prev) => [...prev, result.addAmenity]);
        toast("success", `Successfully saved ${result.addAmenity?.title}`);
        handleCancel();
      },
      onError(e) {
        toast("error", e?.message ?? "Something went wrong!");
      },
    }
  );

  const [updateAmenity, { loading: editAmenityLoading }] = useMutation(
    UPDATE_AMENITY,
    {
      fetchPolicy: "no-cache",
      onCompleted(result) {
        toast("success", `Successfully saved ${result.updateAmenity?.title}`);
        handleCancel();
      },
      onError(e) {
        toast("error", e?.message ?? "Something went wrong!");
      },
    }
  );

  const onChange = (nextPagination, _tableFilters, sorter, extra) => {
    // A new sort order starts again from the first page.
    if (extra?.action === "sort") {
      setSort(toServerSort(sorter, SORTABLE_COLUMNS));
      setPagination((prev) => ({ ...prev, page: 1 }));
      return;
    }
    setPagination((prev) => ({
      ...prev,
      page: nextPagination?.current,
      limit: nextPagination?.pageSize,
    }));
  };

  const handleDelete = async (amenity) => {
    try {
      await updateAmenity({
        variables: {
          amenityInput: {
            _id: amenity._id,
            company: companyId,
            title: amenity.title,
            isActive: false,
          },
        },
      });

      setAmenities((prev) => prev.filter((item) => item._id !== amenity._id));
    } catch (error) {
      console.log("error in amenities/handleDelete");
    }
  };

  const columns = [
    {
      title: "Amenity",
      dataIndex: "title",
      key: "title",
      sorter: true,
      render: (text) => <p>{text}</p>,
    },
    // {
    //   title: "Active",
    //   dataIndex: "isActive",
    //   key: "isActive",
    //   render: (_, record) => checkIsActive(record),
    // },

    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <div className="table-action-btns">
          <Button
            className="border-primary text-primary"
            icon={<EditOutlined />}
            onClick={() => {
              setSelectedAmenity(record.title);
              setSelectAmenityId(record._id);
              showModal();
              setViewMode("Edit");
            }}
          />

          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() =>
              confirm({
                title: `Delete ${record.title}?`,
                description:
                  "This amenity will be removed and can no longer be added to listings.",
                onConfirm: () => handleDelete(record),
              })
            }
          />
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (error) toast("error", error.message);
  }, [error, toast]);

  const firstLoad = loading && !amenities.length;

  return (
    <div>
      <Breadcrumb pageName="Amenities" />
      <TableToolbar
        title="Amenities"
        count={pagination.totalCount || 0}
        searchValue={searchText}
        onSearch={setSearchText}
        action={
          <Button
            className="border-primary text-primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setSelectedAmenity("");
              setSelectAmenityId(null);
              showModal();
              setViewMode("Add");
            }}
          >
            Add New
          </Button>
        }
      />
      <div className="py-6">
        {error && !amenities.length ? (
          <ErrorState onRetry={() => refetch()} />
        ) : (
          <>
            {error && <ErrorBanner onRetry={() => refetch()} />}
            <Table
              scroll={TABLE_SCROLL}
              bordered
              rowKey={(record) => record._id}
              onChange={onChange}
              {...(firstLoad
                ? skeletonTableProps(columns)
                : {
                    columns,
                    dataSource: amenities,
                    pagination: {
                      position: ["bottomCenter"],
                      current: pagination.page,
                      pageSize: pagination.limit,
                      total: pagination.totalCount,
                    },
                  })}
              locale={{
                emptyText: searchText ? (
                  <EmptyState
                    title="No amenities match your search"
                    description={`Nothing found for "${searchText}".`}
                    actionLabel="Clear search"
                    onAction={() => setSearchText("")}
                  />
                ) : (
                  <EmptyState
                    title="No amenities yet"
                    description="Amenities you add will be selectable on listings."
                  />
                ),
              }}
            />
          </>
        )}
      </div>
      <Modal
        open={open}
        title={`Amenity`}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={(_, { OkBtn, CancelBtn }) => (
          <>
            <Button
              type="primary"
              className="border-primary text-primary"
              disabled={
                addAmenityLoading ? true : editAmenityLoading ? true : false
              }
              onClick={async () => {
                if (!selectedAmenity.trim()) return;

                if (viewMode === "Add") {
                  addAmenity({
                    variables: {
                      amenityInput: {
                        company: companyId,
                        title: selectedAmenity,
                        isActive: true,
                      },
                    },
                  });
                } else {
                  await updateAmenity({
                    variables: {
                      amenityInput: {
                        _id: selectAmenityId,
                        company: companyId,
                        title: selectedAmenity,
                        isActive: true,
                      },
                    },
                  });

                  setAmenities((prev) =>
                    prev.map((item) => {
                      if (item._id !== selectAmenityId) return item;
                      return { ...item, title: selectedAmenity };
                    })
                  );
                }
              }}
            >
              Save
            </Button>
            <Button key="back" onClick={handleCancel}>
              Cancel
            </Button>
          </>
        )}
      >
        <Input
          value={selectedAmenity}
          placeholder="Enter New Amenity"
          onChange={(event) => {
            setSelectedAmenity(event.target.value);
          }}
          maxLength={40}
        />
      </Modal>
      {dialog}
    </div>
  );
};

export default Amenities;
